import React, { useContext } from 'react';
import * as R from 'ramda';

import { Collapse, List } from '@atom/mui';
import { CategoryPathItem } from '@atom/types/category';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import { hasElements, isAsset } from './InventoryAssetTreeTransfer';
import LoadAssetButton from './LoadAssetsButton';
import TreeContext, { Tree } from './TreeContext';
import TreeItem from './TreeItem';

export interface Props {
  level: number;
  trees: Tree[];
  path: CategoryPathItem[];
  assetPath: CategoryPathItem[];
}

const getLoadAssetsButtonStyle = (hasChildren: boolean, level: number) =>
  hasChildren
    ? { paddingLeft: `${level + 3}rem` }
    : { paddingLeft: `${level + 5}rem` };

const rollupElementGroups = (tree: any) => {
  return [
    ...tree.elementGroups.reduce((acc, group) => {
      return [...acc, ...group.elements];
    }, []),
    ...tree.elements,
  ];
};

const TreeList = ({ level, trees, path, assetPath }: Props) => {
  const {
    fetchMore,
    getData,
    hasError,
    hasMore,
    isLoading,
    open,
    filterParams,
    removeAssetId,
  } = useContext(TreeContext);

  const { elementSchemaId } = filterParams;

  return (
    <List disablePadding>
      {trees.map(tree => {
        if (tree?.id === removeAssetId) {
          return <div />;
        }

        const hasAssets = !isAsset(tree) && tree.hasAssets;
        const hasElementItems = isAsset(tree) && hasElements(tree);
        const hasCategories = !isAsset(tree) && !!tree.categories?.length;
        const hasChildren =
          hasAssets ||
          hasCategories ||
          (hasElementItems && tree.schemaId !== elementSchemaId);

        const showAssets = hasAssets || hasElementItems;
        const showLoadMore = hasMore(tree.id) && hasAssets;

        const pathValue = [...path, { id: tree.id, name: tree.name }];
        const currentAssetPath = isAsset(tree)
          ? [{ id: tree.id, name: tree.name }]
          : [];
        const assetPathValue = [...assetPath, ...currentAssetPath];
        const nextTrees = hasElementItems
          ? rollupElementGroups(tree)
          : getData(tree.id);

        const showEmpty =
          open.has(tree.id) &&
          isNilOrEmpty(nextTrees) &&
          (hasAssets || hasElementItems);

        return (
          <React.Fragment key={tree.id}>
            <TreeItem
              hasChildren={hasChildren}
              level={level}
              tree={tree}
              path={pathValue}
              assetPath={assetPathValue}
            />
            {showEmpty && (
              <TreeItem
                hasChildren={false}
                level={level}
                tree={{}}
                path={[]}
                assetPath={[]}
              />
            )}
            {hasChildren && !showEmpty && (
              <Collapse in={open.has(tree.id)} timeout="auto" unmountOnExit>
                {showAssets ? (
                  <>
                    {
                      <TreeList
                        level={R.inc(level)}
                        trees={nextTrees}
                        path={pathValue}
                        assetPath={assetPathValue}
                      />
                    }
                    {showLoadMore && (
                      <LoadAssetButton
                        loading={isLoading(tree.id)}
                        error={hasError(tree.id)}
                        onClick={() => fetchMore(tree.id)}
                        style={getLoadAssetsButtonStyle(hasChildren, level)}
                      />
                    )}
                  </>
                ) : (
                  <TreeList
                    level={R.inc(level)}
                    trees={(tree as any).categories}
                    path={pathValue}
                    assetPath={assetPathValue}
                  />
                )}
              </Collapse>
            )}
          </React.Fragment>
        );
      })}
    </List>
  );
};

export default TreeList;
